import { Component } from '@theme/component';
import { wishlist, WISHLIST_CHANGED } from '@theme/wishlist';

/**
 * <wishlist-button> — a heart toggle rendered on product cards and the PDP.
 *
 * Markup (see snippets/mh-wishlist-button.liquid):
 *   <wishlist-button data-product-id data-product-handle data-product-title>
 *     <button ref="btn" on:click="/toggle" aria-pressed
 *             data-label-add data-label-remove> … </button>
 *   </wishlist-button>
 *
 * All buttons for the same product on a page stay in sync because they each
 * re-read the shared store on every `wishlist:change`.
 */
class WishlistButton extends Component {
  requiredRefs = ['btn'];

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(WISHLIST_CHANGED, this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WISHLIST_CHANGED, this.#onChange);
  }

  get #item() {
    const { productId, productHandle, productTitle } = this.dataset;
    return { id: productId, handle: productHandle, title: productTitle };
  }

  #onChange = (event) => {
    const detail = event.detail;
    if (
      !detail ||
      detail.id == null ||
      detail.id === this.dataset.productId ||
      detail.action === 'sync' ||
      detail.action === 'init'
    ) {
      this.#sync();
    }
  };

  #sync = () => {
    const active = wishlist.has(this.dataset.productId);
    const { btn } = this.refs;
    btn.setAttribute('aria-pressed', String(active));
    const label = active ? btn.dataset.labelRemove : btn.dataset.labelAdd;
    if (label) btn.setAttribute('aria-label', label);
    this.classList.toggle('is-active', active);
  };

  /** on:click="/toggle" */
  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    wishlist.toggle(this.#item);
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * <wishlist-icon> — header heart with a live count bubble.
 * Mirrors assets/cart-icon.js.
 */
class WishlistIcon extends Component {
  requiredRefs = ['bubble', 'count'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WISHLIST_CHANGED, this.#onChange);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#render(wishlist.count);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WISHLIST_CHANGED, this.#onChange);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #onChange = (event) => this.#render(event.detail?.count ?? wishlist.count);

  #onPageShow = (event) => {
    if (event.persisted) this.#render(wishlist.count);
  };

  #render = (count) => {
    this.refs.count.textContent = count > 99 ? '99+' : String(count);
    this.refs.bubble.classList.toggle('hidden', count === 0);
    this.classList.toggle('mh-header__icon--has-wishlist', count > 0);
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
